/**
 * Centralized i18n for share texts, share menu labels, and history strings.
 * Detects the user's OS / browser language via `navigator.language`.
 */

const translations = {
  en: {
    // Share text composition
    listeningTo: "I'm listening to",
    by: "by",
    on: "on",
    radioName: "MAJESTIC JUKEBOX RADIO",

    // Share menu
    shareTitle: "Share this track",
    shareNative: "Share via…",
    shareCopy: "Copy link",
    shareCopied: "Copied!",
    shareInstaHint: "Text copied. Paste it into your Instagram story or post.",
    shareTiktokHint: "Text copied. Paste it into your TikTok video.",

    // History
    historyTitle: "History",
    historyEmpty: "No tracks in history yet",
    historyEmptyHint: "Tracks you listen to will appear here",
    historyKept: "The last {count} tracks are kept",
    historyClearConfirm: "Do you really want to clear the entire history?",
    historyClearTitle: "Clear history",
    historyShareTitle: "Share",
    timeNow: "Just now",
    timeMinAgo: "{n} min ago",
    timeHourAgo: "{n}h ago",

    // Update banner
    updateAvailable: "New version available",
    updateRefresh: "Refresh",
  },
  fr: {
    listeningTo: "J'écoute",
    by: "par",
    on: "sur",
    radioName: "MAJESTIC JUKEBOX RADIO",

    shareTitle: "Partager ce morceau",
    shareNative: "Partager via…",
    shareCopy: "Copier le lien",
    shareCopied: "Copié !",
    shareInstaHint: "Texte copié. Collez-le dans votre story ou publication Instagram.",
    shareTiktokHint: "Texte copié. Collez-le dans la description de votre vidéo TikTok.",

    historyTitle: "Historique",
    historyEmpty: "Aucun morceau dans l'historique",
    historyEmptyHint: "Les morceaux écoutés apparaîtront ici",
    historyKept: "Les {count} derniers morceaux sont conservés",
    historyClearConfirm: "Voulez-vous vraiment supprimer tout l'historique ?",
    historyClearTitle: "Supprimer l'historique",
    historyShareTitle: "Partager",
    timeNow: "À l'instant",
    timeMinAgo: "Il y a {n} min",
    timeHourAgo: "Il y a {n}h",
    updateAvailable: "Nouvelle version disponible",
    updateRefresh: "Rafraîchir",
  },
  es: {
    listeningTo: "Estoy escuchando",
    by: "de",
    on: "en",
    radioName: "MAJESTIC JUKEBOX RADIO",

    shareTitle: "Compartir esta canción",
    shareNative: "Compartir vía…",
    shareCopy: "Copiar enlace",
    shareCopied: "¡Copiado!",
    shareInstaHint: "Texto copiado. Pégalo en tu historia o publicación de Instagram.",
    shareTiktokHint: "Texto copiado. Pégalo en la descripción de tu vídeo TikTok.",

    historyTitle: "Historial",
    historyEmpty: "Sin canciones en el historial",
    historyEmptyHint: "Las canciones escuchadas aparecerán aquí",
    historyKept: "Se conservan las últimas {count} canciones",
    historyClearConfirm: "¿Quieres borrar todo el historial?",
    historyClearTitle: "Borrar historial",
    historyShareTitle: "Compartir",
    timeNow: "Ahora mismo",
    timeMinAgo: "Hace {n} min",
    timeHourAgo: "Hace {n}h",
    updateAvailable: "Nueva versión disponible",
    updateRefresh: "Actualizar",
  },
  de: {
    listeningTo: "Ich höre",
    by: "von",
    on: "auf",
    radioName: "MAJESTIC JUKEBOX RADIO",

    shareTitle: "Diesen Titel teilen",
    shareNative: "Teilen via…",
    shareCopy: "Link kopieren",
    shareCopied: "Kopiert!",
    shareInstaHint: "Text kopiert. Füge ihn in deine Instagram-Story oder deinen Beitrag ein.",
    shareTiktokHint: "Text kopiert. Füge ihn in die Beschreibung deines TikTok-Videos ein.",

    historyTitle: "Verlauf",
    historyEmpty: "Noch keine Titel im Verlauf",
    historyEmptyHint: "Gehörte Titel erscheinen hier",
    historyKept: "Die letzten {count} Titel werden gespeichert",
    historyClearConfirm: "Möchtest du den gesamten Verlauf löschen?",
    historyClearTitle: "Verlauf löschen",
    historyShareTitle: "Teilen",
    timeNow: "Gerade eben",
    timeMinAgo: "vor {n} Min.",
    timeHourAgo: "vor {n} Std.",
    updateAvailable: "Neue Version verfügbar",
    updateRefresh: "Aktualisieren",
  },
  it: {
    listeningTo: "Sto ascoltando",
    by: "di",
    on: "su",
    radioName: "MAJESTIC JUKEBOX RADIO",

    shareTitle: "Condividi questo brano",
    shareNative: "Condividi tramite…",
    shareCopy: "Copia link",
    shareCopied: "Copiato!",
    shareInstaHint: "Testo copiato. Incollalo nella tua storia o post Instagram.",
    shareTiktokHint: "Testo copiato. Incollalo nella descrizione del tuo video TikTok.",

    historyTitle: "Cronologia",
    historyEmpty: "Nessun brano nella cronologia",
    historyEmptyHint: "I brani ascoltati appariranno qui",
    historyKept: "Vengono conservati gli ultimi {count} brani",
    historyClearConfirm: "Vuoi davvero cancellare tutta la cronologia?",
    historyClearTitle: "Cancella cronologia",
    historyShareTitle: "Condividi",
    timeNow: "Adesso",
    timeMinAgo: "{n} min fa",
    timeHourAgo: "{n}h fa",
    updateAvailable: "Nuova versione disponibile",
    updateRefresh: "Aggiorna",
  },
  pt: {
    listeningTo: "Estou a ouvir",
    by: "de",
    on: "na",
    radioName: "MAJESTIC JUKEBOX RADIO",

    shareTitle: "Partilhar esta faixa",
    shareNative: "Partilhar via…",
    shareCopy: "Copiar link",
    shareCopied: "Copiado!",
    shareInstaHint: "Texto copiado. Cola no teu story ou post do Instagram.",
    shareTiktokHint: "Texto copiado. Cola na descrição do teu vídeo TikTok.",

    historyTitle: "Histórico",
    historyEmpty: "Sem faixas no histórico",
    historyEmptyHint: "As faixas ouvidas aparecerão aqui",
    historyKept: "As últimas {count} faixas são guardadas",
    historyClearConfirm: "Queres mesmo apagar todo o histórico?",
    historyClearTitle: "Apagar histórico",
    historyShareTitle: "Partilhar",
    timeNow: "Agora mesmo",
    timeMinAgo: "Há {n} min",
    timeHourAgo: "Há {n}h",
    updateAvailable: "Nova versão disponível",
    updateRefresh: "Atualizar",
  },
  nl: {
    listeningTo: "Ik luister naar",
    by: "van",
    on: "op",
    radioName: "MAJESTIC JUKEBOX RADIO",

    shareTitle: "Deel dit nummer",
    shareNative: "Delen via…",
    shareCopy: "Link kopiëren",
    shareCopied: "Gekopieerd!",
    shareInstaHint: "Tekst gekopieerd. Plak deze in je Instagram-verhaal of -bericht.",
    shareTiktokHint: "Tekst gekopieerd. Plak deze in de beschrijving van je TikTok-video.",

    historyTitle: "Geschiedenis",
    historyEmpty: "Nog geen nummers in de geschiedenis",
    historyEmptyHint: "Beluisterde nummers verschijnen hier",
    historyKept: "De laatste {count} nummers worden bewaard",
    historyClearConfirm: "Wil je de hele geschiedenis wissen?",
    historyClearTitle: "Geschiedenis wissen",
    historyShareTitle: "Delen",
    timeNow: "Zojuist",
    timeMinAgo: "{n} min geleden",
    timeHourAgo: "{n}u geleden",
    updateAvailable: "Nieuwe versie beschikbaar",
    updateRefresh: "Vernieuwen",
  },
  ja: {
    listeningTo: "聴いています",
    by: "—",
    on: "—",
    radioName: "MAJESTIC JUKEBOX RADIO",

    shareTitle: "この曲をシェア",
    shareNative: "シェア…",
    shareCopy: "リンクをコピー",
    shareCopied: "コピーしました！",
    shareInstaHint: "テキストをコピーしました。Instagramのストーリーや投稿に貼り付けてください。",
    shareTiktokHint: "テキストをコピーしました。TikTok動画の説明欄に貼り付けてください。",

    historyTitle: "履歴",
    historyEmpty: "履歴に曲がありません",
    historyEmptyHint: "聴いた曲がここに表示されます",
    historyKept: "最新の{count}曲が保存されます",
    historyClearConfirm: "履歴をすべて削除しますか？",
    historyClearTitle: "履歴を削除",
    historyShareTitle: "シェア",
    timeNow: "たった今",
    timeMinAgo: "{n}分前",
    timeHourAgo: "{n}時間前",
    updateAvailable: "新しいバージョンが利用可能",
    updateRefresh: "更新",
  },
};

export const getLocale = () => {
  if (typeof navigator === 'undefined') return 'en';
  const raw = navigator.language || navigator.userLanguage || 'en';
  return raw;
};

export const getLang = () => {
  const raw = getLocale();
  const short = raw.split('-')[0].toLowerCase();
  return translations[short] ? short : 'en';
};

export const t = (key, vars = {}) => {
  const lang = getLang();
  const dict = translations[lang] || translations.en;
  let str = dict[key] != null ? dict[key] : (translations.en[key] || key);
  Object.keys(vars).forEach((k) => {
    str = str.replace(`{${k}}`, vars[k]);
  });
  return str;
};

export const buildShareText = (track) => {
  if (!track || !track.title) return '';
  const dict = translations[getLang()] || translations.en;
  // Format: 🎵 I'm listening to "Title" by Artist on MAJESTIC JUKEBOX RADIO
  return `🎵 ${dict.listeningTo} "${track.title}" ${dict.by} ${track.artist} ${dict.on} ${dict.radioName}`;
};

export default translations;
